import { Cancel, DeleteForever, Edit, Save } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridCellCheckboxRenderer, gridClasses, GridRowEditStopReasons, GridRowModes } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';
import { appendWrapCellEditingClassName, DataGridStyles, wrapCellStyles } from '../../util/DataGridStyles';
import { numberWithCommas } from '../../util/JsonUtils';
import DateCell from '../DateCell';
import { EditableStringCell } from './EditableStringCell';
import { $$campaignVersionsDialog } from './model';
import { $$confirmationDialog } from '../ConfirmationDialog/model.js';
import { DataGrid, Tooltip } from '@ghs/components';
import { $$personaService } from '../../services/PersonaService/model.js';
import { $$app } from 'components/App/model.tsx';

/** @typedef {import('./model').GridRowId} GridRowId */

/**
 * DataGrid's actions-cell mechanism renders whatever `getActions` returns as a direct child of its own
 * root element, and both native Tab and DataGrid's own cell-focus API (`apiRef.setCellFocus`, used e.g.
 * when a cell is clicked or regains focus after a re-render) call `.focus()`/inject tabIndex on that
 * exact top-level DOM node - not on whatever is nested inside it. When that top-level node is
 * `<Tooltip><span><IconButton/></span></Tooltip>`, both mechanisms land on the wrapping <span> instead of
 * the button, so the button needs a second, separate step (Tab press or click) to actually reach it.
 *
 * MUI's Tooltip only needs that extra <span> to work around disabled buttons not firing the mouse/focus
 * events it listens for. So here the <span> is used only while `disabled` is true (and is then the sole
 * focus target, tabIndex 0, letting Tab/click still reveal the tooltip explaining why it's disabled -
 * DataGrid's own cell-focus API already skips disabled actions when auto-focusing, so it never touches
 * this span). Otherwise Tooltip wraps the IconButton directly, so the button itself is the top-level node
 * both focus mechanisms land on - always exactly one focusable stop per action, reached in one step.
 *
 * @param {object} props - Component props
 * @param {string|boolean} [props.title] - Tooltip title; omit to render a bare IconButton with no tooltip
 * @param {boolean} [props.tooltipDisabled] - Passed through as the Tooltip's `disabled` prop
 * @param {boolean} [props.disabled] - Passed through to the IconButton
 * @param {number} [props.tabIndex] - Injected by DataGrid; ignored in favor of the logic described above
 * @param {import('react').Ref<unknown>} [props.touchRippleRef] - Injected by DataGrid for ripple cleanup
 * @returns {React.ReactNode} - JSX Element
 */
const ActionIconButton = ({ title, tooltipDisabled, disabled, tabIndex: _injectedTabIndex, touchRippleRef, ...iconButtonProps }) => {
  const button = <IconButton disabled={disabled} touchRippleRef={touchRippleRef} {...iconButtonProps} tabIndex={0} />;
  if (title === undefined) {
    return button;
  }
  return (
    <Tooltip title={title} placement="bottom" arrow disabled={tooltipDisabled}>
      {disabled ? <span tabIndex={0}>{button}</span> : button}
    </Tooltip>
  );
};

ActionIconButton.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  tooltipDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  touchRippleRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

/**
 * Handles Enter behaving like Tab (Excel-style) on a row's editable cells: move to the next
 * editable cell in the row, or - on the row's last editable cell - save (if needed) and continue
 * into the next row's first cell. Exception: if the cell being left has an unsaved change, Enter
 * instead triggers the same save action as the row's Save button (and stops there), regardless of
 * which column it is.
 *
 * Split out of `handleRowEditStop` purely to keep each function's cognitive complexity manageable;
 * it depends only on `params` and the live-state snapshot, not on anything from the component render.
 *
 * @param {{ id: GridRowId; field: string }} params - The cell where Enter was pressed
 * @param {object} latest - The latest live-state snapshot (see `latestRef` in the component below)
 * @returns {void}
 */
function handleEnterKeyNavigation({ id, field }, latest) {
  if (latest.isValidationPending) {
    return;
  }

  const { apiRef } = latest.dataGridProps;
  const editableColumns = apiRef.current.getAllColumns().filter(column => column.editable);
  const currentColumnIndex = editableColumns.findIndex(column => column.field === field);
  const nextFieldInRow = editableColumns[currentColumnIndex + 1]?.field;

  const isRowWithError = latest.errors.some(e => e.id === id);
  const isCurrentCellChanged = latest.unsavedChanges.some(r => r.id === id && r.field === field);
  if (isCurrentCellChanged && !isRowWithError) {
    $$campaignVersionsDialog.rowFieldPendingRestoreSet({ id, field });
    $$campaignVersionsDialog.singleRowSaveClicked(id);
    return;
  }

  if (nextFieldInRow) {
    apiRef.current.setCellFocus(id, nextFieldInRow);
    return;
  }

  const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(id);
  const nextRowId = apiRef.current.getSortedRowIds()[rowIndex + 1];
  if (nextRowId === undefined || isRowWithError) {
    return;
  }

  const isCurrentRowHasUnsavedChanges = latest.unsavedChanges.some(r => r.id === id);
  if (!isCurrentRowHasUnsavedChanges) {
    $$campaignVersionsDialog.$$versions.setRowMode({ id, mode: GridRowModes.View });
    $$campaignVersionsDialog.$$versions.setRowMode({ id: nextRowId, mode: GridRowModes.Edit });
    apiRef.current.setCellFocus(nextRowId, editableColumns[0].field);
    return;
  }
  $$campaignVersionsDialog.rowFieldPendingRestoreSet({ id, field });
  $$campaignVersionsDialog.nextRowIdAfterSaveSet(nextRowId);
  $$campaignVersionsDialog.singleRowSaveClicked(id);
}

/**
 * CampaignVersionsTable
 *
 * @returns {React.ReactNode} - JSX Element
 */
export default function CampaignVersionsTable() {
  const dataGridProps = $$campaignVersionsDialog.$$versions.useDataGrid();

  const errors = useUnit($$campaignVersionsDialog.$$versions.$errors);
  const highlightedRows = useUnit($$campaignVersionsDialog.$$versions.$highlightedRows);
  const selectedRowIds = useUnit($$campaignVersionsDialog.$$versions.$selectedRowsIds);
  const unsavedChanges = useUnit($$campaignVersionsDialog.$$versions.$unsavedChanges);
  const isValidationPending = useUnit($$campaignVersionsDialog.$isValidationPending);
  const isSavePending = useUnit($$campaignVersionsDialog.$isSavePending);
  const isMapActivePersona = useUnit($$personaService.$isMapActivePersona);
  const userDetails = useUnit($$app.$userDetails);
  const cellPendingFocus = useUnit($$campaignVersionsDialog.$cellPendingFocus);

  // Moves focus onto a cell whose row was just confirmed to be in edit mode: either the next row's
  // first editable cell after an Excel-style Enter-triggered save, or the original cell restored
  // after the user cancels a save prompt (see `handleRowEditStop` below). This has to happen from a
  // React effect - the cell only exists once this component has re-rendered with its new
  // rowModesModel, and the grid api's cell-focus mechanism needs that render to have committed.
  useEffect(() => {
    if (cellPendingFocus === null || dataGridProps.rowModesModel[cellPendingFocus.id]?.mode !== GridRowModes.Edit) {
      return;
    }
    const { apiRef } = dataGridProps;
    const { id } = cellPendingFocus;
    const field = cellPendingFocus.field ?? apiRef.current.getAllColumns().find(column => column.editable)?.field;
    if (field) {
      apiRef.current.setCellFocus(id, field);
      // The save confirmation dialog that led here is still finishing its own closing transition,
      // and MUI's Modal restores focus to whatever was focused before it opened once that transition
      // ends - which would otherwise steal focus right back off the cell we just focused. Deferring
      // past that transition (comfortably longer than MUI's default 225ms leaving-screen duration)
      // lets our focus change win.
      const timeoutId = setTimeout(() => {
        apiRef.current?.getCellElement(id, field)?.querySelector('input')?.focus();
      }, 300);
      $$campaignVersionsDialog.cellPendingFocusCleared();
      return () => clearTimeout(timeoutId);
    }
    $$campaignVersionsDialog.cellPendingFocusCleared();
  }, [cellPendingFocus, dataGridProps.rowModesModel, dataGridProps.apiRef]);

  /**
   * Column definitions read live state through this ref instead of closing over the values above,
   * so `columns` can stay memoized (and its widths untouched by MUI) across renders that don't
   * change the column structure itself, e.g. entering/leaving edit mode.
   */
  const latestRef = useRef({ dataGridProps, errors, unsavedChanges, isValidationPending, isSavePending, isMapActivePersona, userDetails });
  latestRef.current = { dataGridProps, errors, unsavedChanges, isValidationPending, isSavePending, isMapActivePersona, userDetails };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>} */
  const dateColumnProps = {
    field: '',
    editable: true,
    flex: 1,
    valueGetter: params => dayjs(params).toDate(),
    valueFormatter: date => dayjs(date).format('MM/DD/YYYY'),
    renderCell: ({ row, id, field }) => {
      const latest = latestRef.current;
      return (
        <DateCell
          id={id}
          field={field}
          row={latest.dataGridProps.rows.find(r => r.id === id) || row}
          onValueChanged={$$campaignVersionsDialog.$$versions.cellValueChanged}
          errorMessage={latest.errors.find(e => e.id === id && e.field === field)?.error}
          cellMode="view"
        />
      );
    },
    renderEditCell: ({ row, id, field, hasFocus }) => {
      const latest = latestRef.current;
      return (
        <DateCell
          id={id}
          field={field}
          row={latest.dataGridProps.rows.find(r => r.id === id) || row}
          onValueChanged={$$campaignVersionsDialog.$$versions.cellValueChanged}
          errorMessage={latest.errors.find(e => e.id === id && e.field === field)?.error}
          hasFocus={hasFocus}
          cellMode="edit"
        />
      );
    },
    type: 'date'
  };

  const editableStringFieldMaxLengths = { name: 50, number: 20 };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>['renderCell']} */
  const renderCellWithValueTooltip = ({ value }) => (
    <Tooltip title={value ?? ''} arrow>
      <span style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>{value}</span>
    </Tooltip>
  );

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>} */
  const editableStringColumnProps = {
    field: '',
    editable: true,
    flex: 1,
    cellClassName: 'wrap-cell',
    renderCell: params => <EditableStringCell {...params} $$table={$$campaignVersionsDialog.$$versions} />,
    renderEditCell: params => <EditableStringCell {...params} $$table={$$campaignVersionsDialog.$$versions} inputProps={{ maxLength: editableStringFieldMaxLengths[params.field] }} />
  };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>[]} */
  const columns = useMemo(
    () => [
      // MUI's checkbox-selection column normally gives itself a real tabIndex only via DataGrid's own
      // cell-focus API, which this app's Tab handling bypasses in favor of native browser Tab. Forcing
      // tabIndex={0} here keeps the row checkbox a native, always-reachable Tab stop.
      { ...GRID_CHECKBOX_SELECTION_COL_DEF, renderCell: params => <GridCellCheckboxRenderer {...params} tabIndex={0} /> },
      { ...editableStringColumnProps, field: 'name', headerName: 'Version Name' },
      { ...editableStringColumnProps, field: 'number', headerName: 'Version Number' },
      ...(isMapActivePersona
        ? [
            {
              field: 'mspCode',
              headerName: 'MSP',
              flex: 1,
              renderCell: renderCellWithValueTooltip
            }
          ]
        : []),
      { ...dateColumnProps, field: 'firstMailDate', headerName: 'First Mail Date' },
      { ...dateColumnProps, field: 'lastMailDate', headerName: 'Last Mail Date' },
      { ...dateColumnProps, field: 'inHomeStartDate', headerName: 'In-Home Start' },
      { ...dateColumnProps, field: 'inHomeEndDate', headerName: 'In-Home End' },
      {
        field: 'mailClass',
        headerName: 'Mail Class',
        flex: 1,
        valueGetter: params => /** @type {Reseller.Version['mailClass']} */ (params)?.description || '',
        renderCell: renderCellWithValueTooltip
      },
      { field: 'mailQuantity', headerName: 'Mail Qty', flex: 1, valueGetter: numberWithCommas, renderCell: renderCellWithValueTooltip },
      {
        field: 'actions',
        type: 'actions',
        editable: false,
        headerName: 'Actions',
        headerAlign: 'center',
        getActions: ({ id }) => {
          const latest = latestRef.current;
          const isInEditMode = latest.dataGridProps.rowModesModel[id]?.mode === GridRowModes.Edit;
          const version = latest.dataGridProps.rows.find(row => row.id === id);
          const isCellWithError = latest.errors.some(e => e.id === id);
          const isRowHasUnsavedChanges = latest.unsavedChanges.find(r => r.id === id);
          if (!version) {
            return [];
          }
          const isVersionRemovable = !version.hasScans && (!latest.isMapActivePersona || latest.userDetails?.isInternal);
          return isInEditMode
            ? [
                <ActionIconButton
                  key={`version-save-button-${id}`}
                  title={!isCellWithError && (isRowHasUnsavedChanges ? 'Save' : 'No changes to save')}
                  tooltipDisabled={isCellWithError || !isRowHasUnsavedChanges}
                  disabled={isCellWithError || !isRowHasUnsavedChanges}
                  onClick={() => $$campaignVersionsDialog.singleRowSaveClicked(id)}
                >
                  {latest.isValidationPending || latest.isSavePending ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <Save fontSize="small" color={isCellWithError || !isRowHasUnsavedChanges ? 'inherit' : 'primary'} />
                  )}
                </ActionIconButton>,
                <ActionIconButton key={`version-cancel-button-${id}`} title="Cancel" onClick={() => $$campaignVersionsDialog.cancelClicked(id)}>
                  <Cancel fontSize="small" color="error" />
                </ActionIconButton>
              ]
            : [
                <ActionIconButton key={`version-edit-button-${id}`} onClick={() => $$campaignVersionsDialog.editClicked(id)}>
                  <Edit fontSize="small" color="primary" />
                </ActionIconButton>,
                <ActionIconButton
                  key={`version-delete-button-${id}`}
                  title={version.hasScans ? 'Scans are associated with this version and it cannot be deleted' : 'Delete'}
                  tooltipDisabled={!isVersionRemovable}
                  onClick={() => $$campaignVersionsDialog.deleteSingleClicked(id)}
                  disabled={!isVersionRemovable}
                  sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}
                >
                  <DeleteForever fontSize="small" color={!isVersionRemovable ? /** @type {'secondary'} */ ('secondary.enabled') : 'warning'} />
                </ActionIconButton>
              ];
        }
      }
    ],
    // Column structure only needs to change when the MSP column is toggled in/out; every other
    // value used inside the columns (errors, rowModesModel, etc.) is read live via `latestRef` so
    // that unrelated re-renders (e.g. entering edit mode) don't hand MUI a new `columns` reference
    // and cause it to re-derive (and reset) manually resized column widths.
    [isMapActivePersona]
  );

  /** @type {import('@mui/x-data-grid').GridEventListener<'rowEditStop'>} */
  const handleRowEditStop = (params, event) => {
    const { id } = params;
    const latest = latestRef.current;

    if ((params.reason === GridRowEditStopReasons.enterKeyDown || params.reason === GridRowEditStopReasons.rowFocusOut) && $$confirmationDialog.$isOpen.getState()) {
      // Clicking a button inside the save confirmation dialog (Cancel, Save and exit) itself reads
      // as "clicked outside the row" to the DataGrid, since that click lands outside the grid. Without
      // this guard it would re-trigger the very save flow the dialog is already showing, reopening it.
      event.defaultMuiPrevented = true;
      return;
    }

    if (params.reason === GridRowEditStopReasons.enterKeyDown) {
      event.defaultMuiPrevented = true;
      handleEnterKeyNavigation({ id, field: params.field }, latest);
      return;
    }

    if (params.reason !== GridRowEditStopReasons.rowFocusOut) {
      return;
    }
    // The row stays under our control (rowModesModel) instead of letting the DataGrid silently
    // flip it back to view mode on its own.
    event.defaultMuiPrevented = true;
    const isCellWithError = latest.errors.some(e => e.id === id);
    const isRowHasUnsavedChanges = latest.unsavedChanges.some(r => r.id === id);
    if (!isCellWithError && isRowHasUnsavedChanges) {
      $$campaignVersionsDialog.openUnsavedCloseConfirmation({
        saveAll: () => $$campaignVersionsDialog.saveAllClicked(),
        discardChanges: () => $$campaignVersionsDialog.discardClicked(),
        restoreFocusTo: { id, field: params.field }
      });
    }
  };

  return (
    <DataGrid
      data-testid="campaign-versions-table"
      {...dataGridProps}
      columns={columns}
      getRowHeight={() => 'auto'}
      onRowEditStop={handleRowEditStop}
      onRowDoubleClick={({ id }) => $$campaignVersionsDialog.editClicked(id)}
      onCellDoubleClick={(_, event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        setTimeout(() => (target.tagName === 'INPUT' ? target : target.querySelector('input'))?.focus(), 0);
      }}
      pagination
      checkboxSelection={true}
      keepNonExistentRowsSelected={true}
      disableRowSelectionOnClick={true}
      pageSizeOptions={[5, 10, 25, 50]}
      autosizeOnMount={false} // vitest is failing beause of this prop
      autosizeOptions={{
        includeOutliers: true,
        includeHeaders: true
      }}
      initialState={{ columns: { columnVisibilityModel: { id: false } } }}
      getCellClassName={params => {
        const isEnteringEditMode = dataGridProps.rowModesModel[params.id]?.mode === GridRowModes.Edit && params.isEditable === true;
        const priorityClassName = Object.entries({
          [`${gridClasses.cell}--error`]: Boolean(errors.find(e => e.id === params.id && e.field === params.field)),
          'editable-date-cell': isEnteringEditMode,
          'editable-date': dataGridProps.rowModesModel[params.id]?.mode === GridRowModes.Edit && params.colDef.type === 'date',
          [`${gridClasses.cell}--edited`]: unsavedChanges.find(r => r.id === params.id && r.field === params.field),
          [`${gridClasses.cell}--success`]: highlightedRows.includes(params.id),
          [`${gridClasses.cell}--selected`]: selectedRowIds.includes(params.id)
        })
          .filter(([, value]) => value)
          .map(([key]) => key)
          .slice(0, 1)
          .join(' ');
        return appendWrapCellEditingClassName(priorityClassName, isEnteringEditMode);
      }}
      sx={{
        ...DataGridStyles,
        ...wrapCellStyles
      }}
    />
  );
}
